import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

const RANGE_LABELS = { van: "Van chevaux", prosport: "Pro & Sport" };
const DEFAULT_COUNTRIES = ["FR", "CH"];

const chip = (on) => `px-2.5 py-1 rounded-full text-xs border transition-colors ${on ? "bg-primary text-primary-foreground border-primary" : "hover:bg-muted"}`;

const MultiChips = ({ label, values, options, onChange, testid }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
    <span className="text-xs text-muted-foreground w-14">{label}</span>
    <button type="button" onClick={() => onChange([])} data-testid={`${testid}-all`} className={chip(!values.length)}>Tous</button>
    {options.map(([k, l]) => (
      <button key={k} type="button" aria-pressed={values.includes(k)} data-testid={`${testid}-${k}`} className={chip(values.includes(k))}
        onClick={() => onChange(values.includes(k) ? values.filter((x) => x !== k) : [...values, k])}>{l}</button>
    ))}
  </div>
);

const Chips = ({ label, value, options, onChange, testid }) => (
  <div className="flex items-center gap-1.5 flex-wrap" data-testid={testid}>
    <span className="text-xs text-muted-foreground w-14">{label}</span>
    {[["all", "Tous"], ...options].map(([k, l]) => (
      <button key={k} type="button" onClick={() => onChange(k)} data-testid={`${testid}-${k}`} className={chip(value === k)}>{l}</button>
    ))}
  </div>
);

const match = (u, f) => (!f.countries.length || u.countries.some((c) => f.countries.includes(c)))
  && (!f.ranges.length || (u.ranges || []).some((r) => f.ranges.includes(r)))
  && (f.excl === "all" || (f.excl === "yes") === !!u.exclusive);

export const InviteDialog = ({ open, onOpenChange }) => {
  const [list, setList] = useState([]);
  const [names, setNames] = useState({});
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [tracking, setTracking] = useState(null);
  const [f, setF] = useState({ countries: DEFAULT_COUNTRIES, ranges: [], excl: "all" });
  const visible = list.filter((u) => match(u, f));
  const applyFilter = (patch) => {
    const nf = { ...f, ...patch };
    setF(nf);
    setPicked(new Set(list.filter((u) => match(u, nf) && !u.invited_at).map((u) => u.id)));
  };
  const toSend = visible.filter((u) => picked.has(u.id)).map((u) => u.id);
  const countryCodes = [...new Set(list.flatMap((u) => u.countries))].sort((a, b) => (names[a] || a).localeCompare(names[b] || b, "fr"));
  useEffect(() => {
    if (!open) return;
    const f0 = { countries: DEFAULT_COUNTRIES, ranges: [], excl: "all" };
    setF(f0);
    api.get("/countries").then(({ data }) => setNames(Object.fromEntries(data.map((c) => [c.code, c.name])))).catch(() => {});
    api.get("/admin/invitations").then(({ data }) => { setList(data); setPicked(new Set(data.filter((u) => match(u, f0) && !u.invited_at).map((u) => u.id))); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [open]);
  const toggle = (id) => setPicked((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const send = async () => {
    setBusy(true);
    try {
      const ids = new Set(toSend);
      const { data } = await api.post("/admin/invitations", { user_ids: toSend });
      setList((l) => l.map((u) => (ids.has(u.id) ? { ...u, invite_status: "queued" } : u)));
      toast.success(`Envoi lancé pour ${data.queued} revendeur(s) — suivez l'avancement ci-dessous`);
      setTracking(ids);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); setBusy(false); }
  };
  useEffect(() => {
    if (!tracking) return undefined;
    const id = setInterval(async () => {
      try {
        const { data } = await api.get("/admin/invitations");
        setList(data);
        const mine = data.filter((u) => tracking.has(u.id));
        if (mine.every((u) => u.invite_status === "sent" || u.invite_status === "failed")) {
          clearInterval(id); setTracking(null); setBusy(false);
          const ko = mine.filter((u) => u.invite_status === "failed");
          toast.success(`${mine.length - ko.length} invitation(s) envoyée(s)`);
          if (ko.length) toast.error(`Échec : ${ko.map((u) => u.email).join(", ")}`);
        }
      } catch { /* retry next tick */ }
    }, 3000);
    return () => clearInterval(id);
  }, [tracking]);
  const progress = tracking ? list.filter((u) => tracking.has(u.id) && ["sent", "failed"].includes(u.invite_status)).length : 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Envoyer les accès aux revendeurs</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Chaque revendeur coché reçoit un e-mail avec son identifiant et un lien personnel (7 jours) pour choisir son mot de passe. Aucun mot de passe n'est écrit dans l'e-mail.</p>
        <div className="space-y-2 border rounded-md p-3 bg-muted/30">
          <MultiChips label="Pays" values={f.countries} options={countryCodes.map((c) => [c, names[c] || c])} onChange={(countries) => applyFilter({ countries })} testid="invite-filter-country" />
          <MultiChips label="Gamme" values={f.ranges} options={Object.entries(RANGE_LABELS)} onChange={(ranges) => applyFilter({ ranges })} testid="invite-filter-range" />
          <Chips label="Marques" value={f.excl} options={[["yes", "Exclusifs Fautras"], ["no", "Non exclusifs (autres marques)"]]} onChange={(excl) => applyFilter({ excl })} testid="invite-filter-exclusive" />
          <p className="text-[11px] text-muted-foreground">Cliquez sur plusieurs pays ou gammes pour les combiner.</p>
        </div>
        <div className="flex gap-2 text-xs">
          <span className="text-muted-foreground" data-testid="invite-visible-count">{visible.length} revendeur(s) affiché(s)</span>
          <button className="underline" onClick={() => setPicked(new Set(visible.map((u) => u.id)))} data-testid="invite-all">Tout cocher</button>
          <button className="underline" onClick={() => setPicked(new Set(visible.filter((u) => !u.invited_at).map((u) => u.id)))} data-testid="invite-not-yet">Seulement les non invités</button>
          <button className="underline" onClick={() => setPicked(new Set())} data-testid="invite-none">Tout décocher</button>
        </div>
        <div className="divide-y divide-border border rounded-md">
          {!visible.length && <div className="px-3 py-4 text-sm text-muted-foreground" data-testid="invite-empty">Aucun revendeur pour ce filtre.</div>}
          {visible.map((u) => (
            <label key={u.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm cursor-pointer" data-testid={`invite-row-${u.id}`}>
              <Checkbox checked={picked.has(u.id)} onCheckedChange={() => toggle(u.id)} data-testid={`invite-check-${u.id}`} />
              <span className="flex-1 min-w-0 break-words"><b>{u.name}</b> · {u.email}</span>
              <span className="text-xs text-muted-foreground">{u.countries.join(", ")} · {(u.ranges || []).map((r) => RANGE_LABELS[r] || r).join(", ")} · {u.exclusive ? "Exclusif" : "Non exclusif"}</span>
              {tracking?.has(u.id) && <span className={`text-[11px] font-medium ${u.invite_status === "failed" ? "text-destructive" : u.invite_status === "sent" ? "text-emerald-700" : "text-muted-foreground"}`} data-testid={`invite-status-${u.id}`}>
                {u.invite_status === "sent" ? "envoyé" : u.invite_status === "failed" ? "échec" : u.invite_status === "waiting" ? "limite d'envoi atteinte, nouvel essai automatique…" : "en cours…"}</span>}
              {u.invited_at && <span className="text-[11px] text-muted-foreground">invité le {new Date(u.invited_at).toLocaleDateString("fr-FR")}{u.invite_reminded_at ? ` · relancé le ${new Date(u.invite_reminded_at).toLocaleDateString("fr-FR")}` : ""}</span>}
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
          <Button onClick={send} disabled={!toSend.length || busy} className="gap-2" data-testid="invite-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} {tracking ? `Envoi en cours… ${progress}/${tracking.size}` : `Envoyer à ${toSend.length} revendeur(s)`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
